/** @jsx jsx */
import { useContext } from 'react';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import Image from 'assets/Image.svg';
import SVG from 'assets/Lottie.svg';
import PDF from 'assets/PDF_smallIcon.svg';
import Video from 'assets/Video.svg';
import Audio from 'assets/AudioIcon.svg';
import Text from 'assets/Text.svg';
import Embed from 'assets/Embed.svg';
import getBlocksByType from 'services/apiHelpers/getBlocksByType';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import editorContext from 'components/Editor/editorContext';
import { H2 as H2_v2 } from '../EditorUtilities/UI/Headings';

const { lGrey3 } = colors;

const mediaTypes = {
  0: 'Text',
  1: 'Image',
  2: 'Video',
  3: 'SVG',
  4: 'Embed',
  5: 'Audio',
  6: 'PDF',
} as const;

// this type stuff here is a bit of overkill maybe. but as an effort to not change the id's themselves
// do the typescripty work to build on top of the existing pattern / interface

const mediaTypeIDs = Object.values(mediaTypes).map(
  mediaType => `media-${mediaType}` as const
);

type MediaTypeID = typeof mediaTypeIDs[number];

interface Icon {
  id: MediaTypeID;
  src: string;
  isActiveType: boolean;
  hasPermission?: 'ENABLED' | 'LOCKED' | 'HIDDEN';
}

const Icon = ({ id, src, isActiveType, hasPermission }: Icon) => {
  if (hasPermission === 'HIDDEN') {
    return null;
  }

  const isLocked =
    hasPermission === 'LOCKED'
      ? `
    pointer-events: none;
    opacity: 0.25;
  `
      : '';

  return src ? (
    <img
      id={id}
      src={src}
      css={css`
        width: 35px;
        height: 35px;
        cursor: pointer;
        pointer-events: ${isActiveType ? 'none' : 'auto'};
        background: ${isActiveType ? lGrey3 : ''};
        border-radius: 5px;
        padding: 5px;
        transition: all 0.2s;
        &:hover {
          background: ${lGrey3};
        }

        ${isLocked}
      `}
    />
  ) : (
    <div
      id={id}
      className="fas fa-code"
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 35px;
        height: 35px;
        cursor: pointer;
        pointer-events: ${isActiveType ? 'none' : 'auto'};
        background: ${isActiveType ? lGrey3 : ''};
        border-radius: 5px;
        padding: 5px;
        transition: all 0.2s;
        &:hover {
          background: ${lGrey3};
        }

        ${isLocked}
      `}
    />
  );
};

const SetMediaType = ({
  block,
  mediaParent,
}: {
  block?: any;
  mediaParent: any;
}) => {
  const context = useContext(editorContext);
  const { creatorInteractionEmbed } = context?.permissions || {};

  if (!mediaParent) {
    return null;
  }

  const mediaType = mediaParent.props.child;
  const { removeTypes, forceInclude } = mediaParent?.props;

  const isActiveType = index => mediaType === index;

  const interactionParent = getBlocksByType('Interaction').find(
    i => findByKeyVal_V2(i.id, 'id', mediaParent.id) as any
  );

  // all of this is gonna be replaced by removeTypes
  // and we have the hack of forceInclude
  // the problem is having to juggle legacy

  const isInteractionType = (interactionParent, type) =>
    interactionParent ? interactionParent.meta.subType === type : undefined;

  const isFlipCard = isInteractionType(interactionParent, 'FlipCard');
  const isClickAndReveal = isInteractionType(
    interactionParent,
    'ClickAndReveal'
  );
  const isMatching = isInteractionType(interactionParent, 'Matching');
  const isAccordion = isInteractionType(interactionParent, 'Accordion');

  // older modules might not have ' new ' types
  // filter applied
  const availableMediaTypes = mediaParent.children.map(p => {
    const block: any = getBlockById(p.id);
    return block.type.toLowerCase();
  });

  let hasEmbed = availableMediaTypes.includes('embed');
  let hasAudio = availableMediaTypes.includes('audio');
  let hasPDF = availableMediaTypes.includes('pdf');

  const interactionParentSubType: string = interactionParent?.meta?.subType;

  // check that media is a child of feedback
  const interactionFeedback = getBlocksByType('Feedback').find(
    i => findByKeyVal_V2(i.id, 'id', mediaParent.id) as any
  );

  const isChildOfFeedback =
    interactionFeedback &&
    interactionFeedback?.meta?.subType?.toLowerCase() === 'feedback';

  // we're going to allow these media types in all modals/feedback now
  if (isChildOfFeedback) {
    // hasEmbed = false;
    // hasPDF = false;
  }
  //

  return (
    <div
      css={css`
        margin-top: 20px;
        margin-bottom: 20px;
      `}
    >
      <div>
        <H2_v2
          css={css`
            margin: 0 0 5px 0;
          `}
        >
          {mediaTypes[mediaType]}
        </H2_v2>
      </div>

      <div
        css={css`
          display: flex;

          img {
            margin-right: 5px;
          }
        `}
        onClick={(e: any) => {
          const type = e.target.id.split('-')[1];

          if (!type) return;

          const typeIndex = {
            Text: 0,
            Image: 1,
            Video: 2,
            SVG: 3,
            Embed: 4,
            Audio: 5,
            PDF: 6,
          }[type];

          // if we change type to anything other than video. ensure lockDown is false
          // include for audio too
          // if (type.toLowerCase() !== 'video') {
          if (!['video', 'audio'].includes(type.toLowerCase)) {
            setBlockProps(interactionParent, {
              lockDown: false,
            });
          }

          setBlockProps(getBlockById(mediaParent.id), {
            child: typeIndex,
          });

          requestAnimationFrame(() => {
            setCurrentBlock(getBlockById(mediaParent.children[typeIndex].id));
          });
        }}
      >
        <Icon id="media-Text" src={Text} isActiveType={isActiveType(0)} />

        <Icon id="media-Image" src={Image} isActiveType={isActiveType(1)} />

        <Icon id="media-Video" src={Video} isActiveType={isActiveType(2)} />

        {hasAudio && (
          <Icon id="media-Audio" src={Audio} isActiveType={isActiveType(5)} />
        )}

        <Icon id="media-SVG" src={SVG} isActiveType={isActiveType(3)} />

        {!(isAccordion || isFlipCard) && hasPDF && (
          <Icon id="media-PDF" src={PDF} isActiveType={isActiveType(6)} />
        )}

        {!(isAccordion || isFlipCard) && hasEmbed && (
          <Icon
            id="media-Embed"
            src={Embed}
            isActiveType={isActiveType(4)}
            hasPermission={creatorInteractionEmbed}
          />
        )}
      </div>
    </div>
  );
};

export default SetMediaType;
